import React, { PureComponent, memo } from 'react'
import PropTypes from 'prop-types'
import camelCase from 'lodash/camelCase'
import hash from 'object-hash'
import { parseSync as parseSvg } from 'svgson'
import componentPropType from '@signal/prop-types/component'
import isTest from '@signal/utils/env/isTest'
import generateGuid from '@signal/utils/generateGuid'
import { SignalSvg, SignalSvgPropTypes } from './createSvgIcon'

/*
 * hydration utility functions
 */

let nextTestId = 1

/**
 * this function cooks the ids an SVG string before hydration.  It generates guids for all ids embedded in the SVG,
 * to prevent collisions with other ids on the page.  Webpack does something similar for SVGs compiled at build time
 * @param {String} svgString - the SVG markup, as a string
 * @return {String} - the SVG markup, with all IDs replaced as guids
 */
export function cookSvgStringIds(svgString) {
  // find all internal ids and randomize their names, since they often have single-character names
  // this is to avoid collisions with other things on the page, including other SVGs
  const idMatches = svgString.match(/id=["'][^'"]+["']/g)
  if (!idMatches) return svgString

  return idMatches.reduce((str, match) => {
    const guid = isTest() ? `test${nextTestId++}` : generateGuid() // generate better unique id
    const quoteChar = match[3] // single quote or double quote?
    match = match.substring(4, match.length - 1) // extract the ID from inside id="..."

    // replace the actual id= with the new guid
    str = str.replace(
      new RegExp(`id=${quoteChar}${match}${quoteChar}`, 'g'),
      `id=${quoteChar}${guid}${quoteChar}`
    )

    // replace any references to th old id, which should in all cases have a leading # and immediately be followed
    // by a quote or close parenthesis
    const endingChars = [quoteChar, '\\)']
    endingChars.forEach(endChar => {
      str = str.replace(
        new RegExp(`#${match}${endChar}`, 'g'),
        `#${guid}${endChar.replace(/\\/g, '')}` // parenthesis is a special regex char, need to remove escape chars
      )
    })

    return str
  }, svgString)
}

/**
 * this function camelizes all attribute names which React requires to be camelized before they hit the DOM, which
 * is any non aria- or non data- attribute, including all namespaced attributes
 * See https://reactjs.org/blog/2017/09/08/dom-attributes-in-react-16.html
 * @param {Object} attributes - attributes for this SVG element, as yielded by the SVG parser
 * @return {Object} - the same attributes object as passed in, except all keys which need to be camelized will be
 */
export function camelizeAttributes(attributes) {
  return Object.keys(attributes).reduce((sanitized, key) => {
    const camelizedKey = camelCase(key)
    if (
      camelizedKey !== key &&
      !key.startsWith('aria-') &&
      !key.startsWith('data-')
    ) {
      sanitized[camelizedKey] = attributes[key]
      delete sanitized[key]
    }
    return sanitized
  }, attributes)
}

/*
 * classes/functions below this line generate hydrated React SVG markup
 */

/**
 * this function recursively generates React DOM elements from the object yielded by the SVG parser
 * @param {Object} svgObj - an object returned from the SVG parser
 * @param {Number} index - index of this element compared to siblings
 * @return {React.Element} - a React DOM tree recursively generated from the SVG parser results
 */
function hydrateSvg({ attributes, children, name }, index) {
  return React.createElement(
    name,
    {
      className: attributes.fill ? undefined : 'fill', // allow manipulation by fillColor unless it specifies a fill
      key: `${index}_${hash(attributes)}`, // must have a key, which will be a hash generated from attributes
      ...camelizeAttributes(attributes) // appropriate attributes must be camelized
    },
    children.map((props, index) => hydrateSvg(props, index))
  )
}

/**
 * this function returns a React component that can be passed to an MUI SVG component property from an SVG string.
 * It parses the provided SVG string and returns a React component that merges the properties passed to the SVG
 * component (for controlling things like size, color, etc) with the root properties of the SVG element as provided by
 * the SVG parser.
 * @param {String} svgString - the SVG markup, as a string
 * @return {React.Component} - a React Component which can render the hydrated SVG markup
 */
function getHydratedSvg(svgObj) {
  const HydratedSvg = props => {
    const attributes = {
      ...svgObj.attributes, // props from parsed SVG
      ...props // merge in size/color props from consumer
    }
    return hydrateSvg({ ...svgObj, attributes }, 0)
  }
  return memo(HydratedSvg)
}

// this class can render an SVG icon hydrated from a string.  It supports an optional ErrorIcon that can be rendered
// if the provided string cannot be parsed/hydrated
class HydratedSvgIcon extends PureComponent {
  state = { useErrorIcon: false }

  // Backstop for any unexpected throw from hydrateSvg itself. Routine
  // parse / size failures are handled in render() before SignalSvg is
  // mounted so they never reach this path.
  componentDidCatch(err) {
    const { svgString } = this.props
    console.error('unexpected hydrateSvg failure', err, svgString)
    this.setState({ useErrorIcon: true })
  }

  render() {
    const { svgString, ErrorIcon, ...rest } = this.props
    const { useErrorIcon } = this.state

    if (useErrorIcon) {
      return ErrorIcon ? <ErrorIcon {...rest} /> : null
    }

    // Parse here (in the class) rather than inside a child function
    // component: a throw inside a function component surfaces to
    // react-error-overlay in development before any error boundary can
    // suppress it, which would blank the entire app on a single bad
    // icon string. Doing it here lets us short-circuit to ErrorIcon
    // without ever throwing.
    let svgObj
    try {
      svgObj = parseSvg(cookSvgStringIds(svgString))
    } catch (err) {
      console.error('could not parse SVG string', err, svgString)
      return ErrorIcon ? <ErrorIcon {...rest} /> : null
    }

    const {
      attributes: { height, width }
    } = svgObj
    if (
      (height && height.toString() !== '24') ||
      (width && width.toString() !== '24')
    ) {
      console.error(
        'HydratedSvg only supports 24x24 icons; received',
        { width, height },
        svgString
      )
      return ErrorIcon ? <ErrorIcon {...rest} /> : null
    }

    return <SignalSvg {...rest} component={getHydratedSvg(svgObj)} />
  }
}

HydratedSvgIcon.propTypes = {
  ...SignalSvgPropTypes,
  svgString: PropTypes.string.isRequired,
  ErrorIcon: componentPropType
}

export default HydratedSvgIcon
